import React, { useEffect, useRef, useState } from 'react';
import { routeHistory } from '@nackle/biw-web-router';
import { GET_COMPANY_QUERY } from '../gql/queries/GET_COMPANY_QUERY';
import { useLazyQuery } from '@apollo/client';
import auth, { canShowBIWAdminStuff } from '../auth/auth';
import { authService } from '../auth/authService';

export const CompanyContext = React.createContext( null );

// Matches a company UUID only when it appears at the very start of the URL path
// (e.g. /<uuid> or /<uuid>/analytics). A UUID that appears later in the path
// (e.g. /content/edit/<object-uuid>) is an object ID — not a company — and must
// not be matched, or the provider will falsely treat it as a company change.
const COMPANY_ID_PATTERN = /^\/([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})(?:\/|$)/i;

// The company id (`cmp`) inside the current access token — the company the API will
// resolve requests to. Read straight from the token rather than React state because
// Bridge does NOT update its React auth state when we delegate.
const tokenCompanyId = () => {
    const token = authService.getAccessToken();
    try { return JSON.parse( atob( token.split( '.' )[ 1 ] ) ).cmp; }
    // Malformed/absent token: return null. The caller then treats the URL company as
    // "not yet the token's company" and delegates, which is the safe fallback.
    catch { return null; }
};

export function CompanyProvider( { children } ) {

    const [ company, setCompany ] = useState( null );
    const [ loading, setLoading ] = useState( true );
    const loadedCompanyId = useRef( null );

    const [ fetchCompany, { data } ] = useLazyQuery( GET_COMPANY_QUERY, {
        fetchPolicy: 'network-only',
        // Covers a failed company query on either path (delegate or direct): allow a
        // retry on the next route change and don't strand the UI in a loading state.
        onError: () => {
            loadedCompanyId.current = null;
            setLoading( false );
        },
    } );

    // Keep a stable reference to the (otherwise possibly re-created) Apollo execute
    // function so the route-listener effect can run once and never re-register.
    const fetchCompanyRef = useRef( fetchCompany );
    useEffect( () => { fetchCompanyRef.current = fetchCompany; }, [ fetchCompany ] );

    function changeCompany( coId ) {
        if ( coId ) {
            setLoading( true );
            fetchCompany();
        }
    }

    // CompanyProvider is the single owner of "which company's variables are loaded".
    // The company being viewed is the one in the URL; (re)load its variables whenever
    // that changes — initial load, reload, or a company switch.
    //
    // For a BIW admin masquerading into a client the access token must first be
    // delegated to that company. auth.delegate() swaps the access token but does NOT
    // update Bridge's React auth state, so we read the company from the token and fetch
    // only AFTER delegation resolves. Until then `loading` stays true (spinner), so the
    // home company's variables are never fetched and the menu never flashes.
    useEffect( () => {
        const loadCompanyForUrl = () => {
            const match = window.location.pathname.match( COMPANY_ID_PATTERN );
            const urlCompany = match ? match[ 1 ] : '';

            if ( urlCompany === loadedCompanyId.current ) { return; }
            loadedCompanyId.current = urlCompany;
            setLoading( true );

            if ( !urlCompany ) {
                // No UUID in URL (root path or one-company environment) — fetch using
                // the current token's company directly; no delegation needed.
                fetchCompanyRef.current();
                return;
            }

            const mustDelegate = canShowBIWAdminStuff()
                && authService.isAdministrator()
                && urlCompany !== tokenCompanyId();

            if ( mustDelegate ) {
                auth.delegate( urlCompany )
                    .then( () => fetchCompanyRef.current() )
                    .catch( () => {
                        // Delegation (auth) failed — allow a retry and release the
                        // loading state. Query failures are handled by onError above.
                        loadedCompanyId.current = null;
                        setLoading( false );
                    } );
            } else {
                fetchCompanyRef.current();
            }
        };

        routeHistory.addEventListener( 'changed', loadCompanyForUrl );
        routeHistory.refresh();

        return () => routeHistory.removeEventListener( 'changed', loadCompanyForUrl );
    }, [] );

    useEffect( () => {
        if ( !data ) {return;}
        setCompany( data.company );
        setLoading( false );
    }, [ data ] );

    return <CompanyContext.Provider value={ { loading, company, changeCompany } }>{ children }</CompanyContext.Provider>;
}
